import ThemeBtn from '@/components/antd-comp/theme-btn'
import { Button, Modal } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import './index.less'
/**
 * 业务模态框组件
 *
 * 这是一个基于Ant Design Modal封装的业务模态框组件，提供了统一的样式和交互体验。
 * 支持自定义标题、内容、按钮文字等，适用于各种业务场景的弹窗需求。
 *
 * @param title - 模态框标题
 * @param content - 模态框内容，可以是任何React元素
 * @param width - 模态框宽度，默认为600px
 * @param onCancel - 取消按钮点击回调函数
 * @param onOk - 确认按钮点击回调函数
 * @param okText - 确认按钮文字，默认为国际化文本
 * @param cancelText - 取消按钮文字，默认为国际化文本
 * @param selfFooter - 自定义底部；不传则用默认确认/取消；传 `null` 则不展示底部栏
 * @param props - 其他传递给Modal组件的属性
 * @param setClosable - 是否显示关闭按钮，默认为true
 *
 * 使用场景：
 * 1. 表单提交确认 - 用户操作前的二次确认
 * 2. 信息展示 - 显示详细信息或帮助内容
 * 3. 数据编辑 - 编辑表单的弹窗展示
 * 4. 删除确认 - 危险操作的确认提示
 * 5. 设置配置 - 各种配置项的弹窗设置
 * 6. 文件上传 - 文件选择和上传的弹窗
 * 7. 用户选择 - 选择用户、角色等的弹窗
 * 8. 系统提示 - 系统消息和通知的展示
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * const [visible, setVisible] = useState(false)
 *
 * {visible && (
 *   <ModalComponent
 *     title="确认操作"
 *     content={
 *       <div>
 *         <p>您确定要执行此操作吗？</p>
 *       </div>
 *     }
 *     onCancel={() => setVisible(false)}
 *     onOk={() => {
 *       // 执行操作
 *       setVisible(false)
 *     }}
 *   />
 * )}
 *
 * // 自定义样式和按钮
 * <ModalComponent
 *   title="编辑用户信息"
 *   width={800}
 *   content={<UserEditForm />}
 *   okText="保存"
 *   cancelText="取消"
 *   onCancel={() => setVisible(false)}
 *   onOk={handleSave}
 * />
 *
 * // 自定义底部按钮
 * <ModalComponent
 *   title="自定义操作"
 *   content={<div>内容</div>}
 *   selfFooter={[
 *     <Button key="custom1">自定义按钮1</Button>,
 *     <Button key="custom2">自定义按钮2</Button>
 *   ]}
 *   onCancel={() => setVisible(false)}
 * />
 * ```
 */
const ModalComponent = ({
  title,
  content,
  width = 600,
  onCancel,
  onOk,
  okText,
  cancelText,
  selfFooter,
  setClosable = true,
  ...props
}: any) => {
  // 模态框显示状态，默认为true（组件挂载时显示）
  const [isModalVisible, setIsModalVisible] = useState(true)
  // 国际化翻译函数
  const { t } = useTranslation()
  return (
    <>
      <Modal
        title={title}
        open={isModalVisible}
        onCancel={onCancel}
        width={width}
        className="modal_antcomp_case max-h-[40%]"
        style={{ top: '10%' }}
        maskClosable={false}
        closable={setClosable}
        footer={
          selfFooter === undefined
            ? [
                <Button key="cancel" className="closeBtnStyles closeBtnWid" shape="round" onClick={onCancel}>
                  {cancelText || t('cancel')}
                </Button>,
                <ThemeBtn key="create" className="closeBtnWid" onClick={onOk}>
                  {okText || t('create')}
                </ThemeBtn>,
              ]
            : selfFooter
        }
        {...props}
      >
        {content}
      </Modal>
    </>
  )
}

export default ModalComponent
